import { stripUrlParams } from "utils/utils";

/**
 * Copy and paste for note/comment attachments.
 *
 * Pasting a file or screenshot uploads it like a picked file. Copying an attachment puts its link
 * on the system clipboard and remembers it here, so pasting that exact link into another composer
 * attaches the already-uploaded file by reference instead of dropping the URL into the text. The
 * backend strips the presign signature on write, so the reference outlives the link's expiry.
 */

const COPIED_ATTACHMENT_KEY = "kothar.copiedAttachment";
const GENERIC_CLIPBOARD_NAMES = new Set(["image.png", "image.jpg", "image.jpeg", "image.gif"]);
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp"];

let copiedInMemory = null;

const pad = (value) => String(value).padStart(2, "0");

const timestamp = (date) =>
  `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(
    date.getHours()
  )}${pad(date.getMinutes())}${pad(date.getSeconds())}`;

const extensionOf = (file) => {
  const fromType = String(file.type || "").split("/")[1];
  if (fromType) return fromType === "jpeg" ? "jpg" : fromType;
  return (
    String(file.name || "")
      .split(".")
      .pop() || "png"
  );
};

/** Screenshots arrive as a bare "image.png"; every one pasted would collide on the same name. */
const withReadableName = (file, index, now) => {
  if (file.name && !GENERIC_CLIPBOARD_NAMES.has(file.name.toLowerCase())) return file;
  const suffix = index > 0 ? `-${index + 1}` : "";
  const name = `pasted-image-${timestamp(now)}${suffix}.${extensionOf(file)}`;
  return new File([file], name, { type: file.type, lastModified: file.lastModified });
};

export const filesFromClipboard = (clipboardData) => {
  const items = Array.from(clipboardData?.items || []);
  const now = new Date();
  return items
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter(Boolean)
    .map((file, index) => withReadableName(file, index, now));
};

const readStoredCopy = () => {
  try {
    return window.localStorage.getItem(COPIED_ATTACHMENT_KEY);
  } catch {
    return null;
  }
};

const rememberCopiedAttachment = (url) => {
  copiedInMemory = url;
  try {
    window.localStorage.setItem(COPIED_ATTACHMENT_KEY, url);
  } catch {
    // Private windows can refuse storage; the in-memory copy still covers this tab.
  }
};

/** The attachment a pasted text refers to, when it is exactly a link copied with the copy button. */
export const copiedAttachmentFor = (text) => {
  const pasted = String(text || "").trim();
  if (!pasted) return null;
  return [copiedInMemory, readStoredCopy()].find((copied) => copied && copied === pasted) || null;
};

/**
 * onPaste handler for a composer. `onFiles(files)` receives pasted files to upload;
 * `onLinks(urls)` receives copied attachments to attach as they are. Composers that can only take
 * fresh uploads omit `onLinks`, and a copied link then pastes as ordinary text.
 */
export const handleAttachmentPaste = (event, { onFiles, onLinks } = {}) => {
  const data = event?.clipboardData;
  if (!data) return;
  const text = data.getData("text/plain");

  const copied = onLinks ? copiedAttachmentFor(text) : null;
  if (copied) {
    event.preventDefault();
    onLinks([copied]);
    return;
  }

  const files = filesFromClipboard(data);
  if (!files.length || !onFiles) return;
  onFiles(files);

  // A file copied in the OS brings its own name along as text; only real text should still paste.
  const pastedText = String(text || "").trim();
  const isJustTheFileName = files.some((file) => file.name === pastedText);
  if (!pastedText || isJustTheFileName) event.preventDefault();
};

const writeTextFallback = (text) => {
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(area);
  }
};

export const copyAttachmentLink = async (url) => {
  rememberCopiedAttachment(url);
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }
  if (!writeTextFallback(url)) throw new Error("Clipboard is not available");
};

export const isImageAttachment = (url) =>
  IMAGE_EXTENSIONS.includes(
    stripUrlParams(url || "")
      .split(".")
      .pop()
      ?.toLowerCase()
  );

/**
 * Browsers only accept PNG image data on the clipboard, so other formats are redrawn as PNG.
 * The fetch skips the HTTP cache: the preview <img> loaded the same URL without an Origin header,
 * S3 answered that without CORS headers, and reusing that cached response fails the CORS check.
 */
const fetchAsPng = async (url) => {
  const response = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
  if (!response.ok) throw new Error(`Could not load image (${response.status})`);
  const blob = await response.blob();
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (png) => (png ? resolve(png) : reject(new Error("PNG encoding failed"))),
      "image/png"
    )
  );
};

/**
 * Copies an attachment. Images also go on the clipboard as picture data so they paste into other
 * apps; that needs the storage bucket to allow the fetch, so any failure falls back to the link.
 * Resolves to "image" or "link" for the confirmation message.
 */
export const copyAttachment = async (url) => {
  if (!url) throw new Error("Nothing to copy");
  const canCopyImage =
    isImageAttachment(url) &&
    typeof window.ClipboardItem !== "undefined" &&
    Boolean(navigator.clipboard?.write);

  if (canCopyImage) {
    try {
      rememberCopiedAttachment(url);
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "image/png": fetchAsPng(url),
          "text/plain": new Blob([url], { type: "text/plain" }),
        }),
      ]);
      return "image";
    } catch {
      // Fall through: the link alone still pastes as an attachment inside the app.
    }
  }

  await copyAttachmentLink(url);
  return "link";
};

/** Every stored attachment shape (array, JSON array string, single link) as a list of links. */
export const parseAttachmentList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  const text = String(value).trim();
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      return [text];
    }
  }
  return text ? [text] : [];
};

/** Inverse of parseAttachmentList for single-column stores: one link plainly, several as JSON. */
export const serializeAttachmentList = (urls) => {
  const list = (urls || []).filter(Boolean);
  if (!list.length) return null;
  return list.length === 1 ? list[0] : JSON.stringify(list);
};
